// =============================================================================
// src/features/explore/constants.ts — Tabelas estaticas da tela "Explorar".
//
// Proveniencia: assets/explore.js linhas 17-86 (PARTY_COLORS, RAMPS, METRIC_META,
//   BUCKET_LABELS, POLITICAL, THEMES, ZONE_THEME, CAT_META, ACAO_META, MAJOR_LABELS
//   + as constantes de topo GEO_MUN/GEO_BAIRROS/MANAUS_ID/YEARS/ZONES/ZOOM_THRESHOLD).
//   Portado VERBATIM — cores, ordens e limiares sao paridade visual; NAO alterar.
//
// SRP: so dados estaticos da feature. Sem DOM, sem Leaflet, sem regra de negocio.
//   Os literais de dominio (anos, zonas, regioes) referenciam os tipos do contrato
//   para nao divergir do dataset (src/app/types.ts).
// =============================================================================

import type { PathOptions } from 'leaflet';
import type {
  ElectoralYear,
  ZonaKey,
  HexColor,
  ExplorePoiCategory,
  ExploreActionType,
  ExploreThematicAxis,
  ExploreCampaignAlignment,
  ExploreCampaignMarkerKind,
  MunicipalityId,
} from '../../app/types';
import { EXPLORE_DATASET } from '../../data';

/**
 * Metricas de coloracao do mapa. `'nenhum'` = mapa limpo (sem coropleto); a camada
 * so "surge" quando o usuario aplica um filtro. As demais cobrem resultado eleitoral,
 * perfil do territorio, os 7 eixos tematicos e as 3 camadas de campanha.
 * diverge do golden por decisao de produto 2026-05-31 (default era 'partido').
 */
export type ExploreColorMetric =
  | 'nenhum'
  | 'partido'
  | 'densidade'
  | ExploreThematicAxis
  | 'idh'
  | 'populacao'
  | 'competitividade'
  | 'segundo'
  | 'alinhamento'
  | 'cobertura'
  | 'reduto'
  // diverge do golden por decisao de produto 2026-06-01 — indices estrategicos (heuristica).
  | 'volatilidade'
  | 'variacao2422'
  | 'campoBatalha';

/**
 * Caminho do GeoJSON de municipios do AM (assets/explore.js linha 17). Desde
 * 2026-10-13 e a malha OFICIAL do IBGE em qualidade maxima, gerada por
 * scripts/build-official-geo.mjs (o arquivo herdado desviava ate ~1 km nas divisas).
 */
export const GEO_MUN_URL = 'assets/geo-amazonas-municipios.json';
/**
 * Mesma malha do IBGE em qualidade intermediaria (~190 KB gzip vs ~590 KB) para o
 * mini-mapa do Briefing — a landing nao paga o peso da malha maxima (2026-10-13).
 */
export const GEO_MUN_LITE_URL = 'assets/geo-amazonas-municipios-lite.json';
/** Caminho do GeoJSON de bairros de Manaus (assets/explore.js linha 18). */
export const GEO_BAIRROS_URL = 'assets/geo-manaus-bairros.json';
/**
 * Caminho do contorno do estado (perimetro dissolvido dos 62 municipios). Gerado
 * por scripts/build-contour.mjs. diverge do golden por decisao de produto 2026-05-31.
 */
export const GEO_CONTOUR_URL = 'assets/geo-amazonas-contorno.json';
/**
 * Zonas eleitorais do TSE no AM (camada "Zonas eleitorais", off por padrao). Gerado
 * por scripts/build-tse-zones.mjs a partir dos locais de votacao do TSE 2024 + setores
 * censitarios do IBGE 2022 — area APROXIMADA (o TSE nao publica poligono de zona).
 */
export const GEO_TSE_ZONES_URL = 'assets/geo-am-zonas-eleitorais.json';
/**
 * Estilo do limite administrativo do AM: linha continua stone, discreta.
 * Reforma 2026-06 (D2): o tracejado vermelho estilo Google Maps lia como
 * ERRO/alerta na primeira impressao — limite institucional e quieto.
 */
export const STATE_OUTLINE_STYLE: PathOptions = {
  color: '#5F5E5A', weight: 1.5, opacity: 0.7, fill: false,
};
/** Id IBGE da capital — recebe close-up em zonas e maxZoom proprio. */
export const MANAUS_ID: MunicipalityId = '1302603';
/** Anos cobertos pelo filtro de ano (assets/explore.js linha 20). */
export const EXPLORE_YEARS: readonly ElectoralYear[] = ['2014', '2016', '2018', '2020', '2022', '2024'];

/**
 * Cargo eletivo por ano — modelo "misto rotulado". So 2016/2020/2024 sao eleicoes
 * MUNICIPAIS (prefeito); 2014/2018/2022 sao gerais (governador). Define o cargo que
 * o TSE deve agregar por ano (scripts/build-explore-data.mjs) e o rotulo na UI.
 */
export const ELECTION_OFFICE: Record<ElectoralYear, 'prefeito' | 'governador'> = {
  '2014': 'governador', '2016': 'prefeito', '2018': 'governador',
  '2020': 'prefeito', '2022': 'governador', '2024': 'prefeito',
};

/** Rotulo legivel de cada cargo (chips/menus/legenda). */
export const OFFICE_LABEL: Record<'prefeito' | 'governador', string> = {
  prefeito: 'Prefeito', governador: 'Governador',
};

/** Rotulo combinado ano + cargo, ex.: "2024 · Prefeito". */
export function electionYearLabel(year: ElectoralYear): string {
  return `${year} · ${OFFICE_LABEL[ELECTION_OFFICE[year]]}`;
}

/**
 * Marcador de PROCEDENCIA (apenas no codigo, nao aparece na tela): `true` quando ha
 * resultado `el` oficial (TSE via backend AtalaIA). scripts/build-explore-data.mjs
 * vira para `true` ao injetar o dado. A granularidade por ano vive em ELECTORAL_REAL_YEARS.
 */
export const ELECTORAL_DATA_REAL = true;
/**
 * Anos cujos resultados `el` ja vem de fonte OFICIAL (TSE via backend AtalaIA).
 * Os demais seguem ESTIMATIVA: 2014/2018 (Governador) ainda nao foram carregados
 * no backend. scripts/build-explore-data.mjs reescreve esta lista ao injetar dado real.
 */
export const ELECTORAL_REAL_YEARS: readonly ElectoralYear[] = ['2016', '2020', '2022', '2024'];
/**
 * Eixos `th` cujo nivel (0..3) vem de INDICADOR real do backend AtalaIA, por quartil:
 * educacao=IDEB, renda=RDPC, vulnerabilidade=cobertura Bolsa Familia. Os demais
 * (religiao/comercio/seguranca/pertencimento) seguem ESTIMATIVA — sem fonte ainda.
 * scripts/build-explore-data.mjs reescreve esta lista ao derivar os eixos.
 */
export const THEMATIC_REAL_AXES: readonly ExploreThematicAxis[] = ['educacao', 'renda', 'vulnerabilidade'];
/** As 6 zonas de Manaus, na ordem do close-up (assets/explore.js linha 21). */
export const EXPLORE_ZONES: readonly ZonaKey[] = ['norte', 'sul', 'leste', 'oeste', 'centro-oeste', 'centro-sul'];
/** Limiar de zoom que troca municipios <-> zonas de Manaus (assets/explore.js 23). */
export const EXPLORE_ZOOM_THRESHOLD = 9;

/** Cores fixas por partido (assets/explore.js 30-33) — paridade de dado. */
export const PARTY_COLORS: Record<string, HexColor> = {
  Avante: '#1D9E75', PL: '#185FA5', PSD: '#378ADD', MDB: '#639922', PT: '#E24B4A',
  PSDB: '#7F77DD', UB: '#6D4FB3', Republicanos: '#1E8A8F', PP: '#B5651D', PSB: '#E0922A',
};

/** Ordem canonica dos partidos na legenda/menu (assets/explore.js 383/420). */
export const PARTY_ORDER: readonly string[] = ['Avante', 'PL', 'PSD', 'MDB', 'PT', 'PSDB', 'UB', 'Republicanos', 'PP', 'PSB'];

/** Rampas de 4 baldes (0..3) por metrica (assets/explore.js 36-45). */
export const RAMPS: Record<string, readonly HexColor[]> = {
  educacao:        ['#E3F0DC', '#A9D08A', '#6FB04A', '#3B6D11'],
  renda:           ['#FAEEDA', '#F3CE86', '#E0A53D', '#9A6512'],
  religiao:        ['#E7E4F6', '#B7AEE6', '#7E72CF', '#534AB7'],
  comercio:        ['#FCE3D6', '#F4B48C', '#E07B4A', '#A8431B'],
  seguranca:       ['#DCEBF4', '#9CC4DF', '#5A93C2', '#0C447C'],
  vulnerabilidade: ['#FCEBEB', '#F09595', '#E24B4A', '#A32D2D'],
  pertencimento:   ['#C0DD97', '#97C459', '#639922', '#3B6D11'],
  densidade:       ['#E6F1FB', '#9CC4DF', '#5A93C2', '#0C447C'],
  // diverge do golden por decisao de produto 2026-05-31 — novas metricas de cor.
  idh:             ['#E3F2EF', '#9FD5C9', '#4FA391', '#1C6B5A'],
  populacao:       ['#ECEFF3', '#B7C2D0', '#7488A0', '#3C5068'],
  // Rampa DIVERGENTE: disputado (warm) -> dominante (cool). 0=<45% ... 3=>55%.
  competitividade: ['#D73027', '#FC8D59', '#91BFDB', '#4575B4'],
  // Campanha: cobertura de campo (verde sequencial) e força de reduto (azul sequencial).
  cobertura:       ['#EAF4EC', '#A9D0B0', '#5FAE75', '#1D7A45'],
  reduto:          ['#EAF0F8', '#AEC4E6', '#5E83C8', '#26478F'],
  // Indices estrategicos (decisao de produto 2026-06-01): violeta / magenta / ciano-teal,
  // matizes em slots vazios da roda p/ nao colidir com idh/renda/densidade/competitividade.
  volatilidade:    ['#EFE6FA', '#C9ADEE', '#9B6FDC', '#6B2FB0'],
  variacao2422:    ['#FCE4F0', '#F3A9CC', '#E162A0', '#B11E6B'],
  campoBatalha:    ['#E7EEF0', '#A8CDD6', '#5FA0B0', '#1C6E80'],
};

/** Rotulo (e titulo de legenda) por metrica (assets/explore.js 46-56). */
export const METRIC_META: Record<string, { readonly label: string; readonly legendTitle?: string }> = {
  // diverge do golden por decisao de produto 2026-05-31 — modo limpo e metricas novas.
  nenhum:          { label: 'Nenhuma (limpo)' },
  partido:         { label: 'Partido dominante' },
  segundo:         { label: '2º colocado', legendTitle: '2º mais votado' },
  competitividade: { label: 'Competitividade', legendTitle: 'Competitividade (% do 1º)' },
  populacao:       { label: 'População', legendTitle: 'População (mil hab)' },
  idh:             { label: 'IDH', legendTitle: 'IDH municipal' },
  alinhamento:     { label: 'Alinhamento', legendTitle: 'Alinhamento político' },
  cobertura:       { label: 'Cobertura de campo', legendTitle: 'Cobertura de campo' },
  reduto:          { label: 'Reduto eleitoral', legendTitle: 'Força de reduto' },
  volatilidade:    { label: 'Volatilidade', legendTitle: 'Volatilidade partidária (2014–24)' },
  variacao2422:    { label: 'Variação 24×22', legendTitle: 'Variação do vencedor (2022→2024)' },
  campoBatalha:    { label: 'Campo de batalha', legendTitle: 'Campo de batalha (prioridade · heurística)' },
  densidade:       { label: 'Densidade de eleitores', legendTitle: 'Eleitores (mil)' },
  vulnerabilidade: { label: 'Vulnerabilidade social', legendTitle: 'Vulnerabilidade social' },
  pertencimento:   { label: 'Pertencimento', legendTitle: 'Pertencimento' },
  educacao:        { label: 'Educação', legendTitle: 'Educação (IDEB)' },
  renda:           { label: 'Renda', legendTitle: 'Renda domiciliar' },
  religiao:        { label: 'Religião', legendTitle: 'Presença religiosa' },
  comercio:        { label: 'Comércio', legendTitle: 'Densidade de comércio' },
  seguranca:       { label: 'Segurança', legendTitle: 'Índice de segurança' },
};

/** Rotulos dos 4 baldes na legenda tematica (assets/explore.js 57). */
export const BUCKET_LABELS: readonly string[] = ['Baixa', 'Média', 'Alta', 'Muito alta'];

/**
 * Metricas-radio do painel "Camadas", agrupadas. `'nenhum'` abre o mapa limpo;
 * vulnerabilidade/pertencimento sairam para o chip "Tema" (agora 7 eixos).
 * diverge do golden por decisao de produto 2026-05-31 (era POLITICAL_METRICS unico).
 */
export const RESULTADO_METRICS: readonly ExploreColorMetric[] = ['nenhum', 'partido', 'segundo', 'competitividade'];
/** Grupo "Perfil do território" do painel. */
export const TERRITORIO_METRICS: readonly ExploreColorMetric[] = ['densidade', 'populacao', 'idh'];
/** Grupo "Campanha" do painel (camada de campanha derivada). */
export const CAMPANHA_METRICS: readonly ExploreColorMetric[] = ['alinhamento', 'cobertura', 'reduto'];

/**
 * Grupo "Estratégico" do painel — índices DERIVADOS (heurística sobre a série 2014–24).
 * NÃO é "preditivo" (corte do auditor 2026-06-01): é leitura do passado p/ priorizar.
 */
export const ESTRATEGICO_METRICS: readonly ExploreColorMetric[] = ['volatilidade', 'variacao2422', 'campoBatalha'];

/**
 * Eixos tematicos do dropdown "Tema" — os 7 do dataset.
 * diverge do golden por decisao de produto 2026-05-31 (eram 5; +vuln/+pertencimento).
 */
export const THEME_METRICS: readonly ExploreThematicAxis[] = [
  'educacao', 'renda', 'religiao', 'comercio', 'seguranca', 'vulnerabilidade', 'pertencimento',
];

/**
 * Microrregioes distintas do AM (recorte do chip "Microrregião"), derivadas do
 * dataset e ordenadas. diverge do golden por decisao de produto 2026-05-31.
 */
export const MICRO_LIST: readonly string[] = Array.from(
  new Set(Object.values(EXPLORE_DATASET.munis).map((m) => m.micro)),
).sort((a, b) => a.localeCompare(b, 'pt-BR'));

/** Item de faixa de eleitorado (recorte do chip "Faixa de eleitorado"). */
export interface EleitoradoBandItem {
  readonly value: string;
  readonly label: string;
}

/**
 * Faixas de eleitorado (sobre eleitoresK, em milhares). Pareadas com eleitoradoBandOf.
 * diverge do golden por decisao de produto 2026-05-31.
 */
export const ELEITORADO_BANDS: readonly EleitoradoBandItem[] = [
  { value: 'baixo', label: 'Até 10 mil' },
  { value: 'medio', label: '10–50 mil' },
  { value: 'alto', label: 'Mais de 50 mil' },
];

/** Cor fixa por alinhamento de campanha (metrica 'alinhamento'). */
export const ALIGN_COLORS: Record<ExploreCampaignAlignment, HexColor> = {
  aliado: '#1D9E75', neutro: '#9AA3AE', oposicao: '#E24B4A',
};

/** Rotulo/cor de cada faixa de alinhamento, na ordem da legenda. */
export const ALIGN_LEGEND: readonly (readonly [label: string, value: ExploreCampaignAlignment])[] = [
  ['Aliado', 'aliado'], ['Neutro', 'neutro'], ['Oposição', 'oposicao'],
];

/** Rotulo/icone/cor de cada marcador de campanha (toggles "Lideranças"/"Doadores"). */
export const CAMPAIGN_MARKER_META: Record<ExploreCampaignMarkerKind, { readonly label: string; readonly icon: string; readonly color: HexColor }> = {
  lideranca: { label: 'Lideranças', icon: 'ti-users-group', color: '#185FA5' },
  doador:    { label: 'Doadores',   icon: 'ti-cash',         color: '#1D9E75' },
};

/**
 * Toggle "Zonas eleitorais (TSE)" do painel de Camadas (2026-10-13). Cor = mesmo
 * azul-ardosia do contorno tracejado (TSE_ZONE_STYLE em explore-tse-zone-layer).
 */
export const TSE_ZONE_TOGGLE_META: { readonly label: string; readonly icon: string; readonly color: HexColor } = {
  label: 'Zonas eleitorais (TSE)', icon: 'ti-border-outer', color: '#1E3A5F',
};
/** Toggle da camada de locais de votacao 2026 (TSE, votacao por secao; 2026-10-13). */
export const G26_LOCAIS_TOGGLE_META: { readonly label: string; readonly icon: string; readonly color: HexColor } = {
  label: 'Locais de votação 2026 (TSE)', icon: 'ti-building-community', color: '#1E3A5F',
};

/** Cores por zona no close-up de Manaus, por tema (assets/explore.js 62-68). */
export const ZONE_THEME: Record<string, Record<ZonaKey, HexColor>> = {
  educacao:  { norte: '#6FB04A', sul: '#A9D08A', leste: '#3B6D11', oeste: '#A9D08A', 'centro-oeste': '#6FB04A', 'centro-sul': '#3B6D11' },
  renda:     { norte: '#F3CE86', sul: '#E0A53D', leste: '#FAEEDA', oeste: '#F3CE86', 'centro-oeste': '#E0A53D', 'centro-sul': '#9A6512' },
  religiao:  { norte: '#7E72CF', sul: '#B7AEE6', leste: '#534AB7', oeste: '#7E72CF', 'centro-oeste': '#B7AEE6', 'centro-sul': '#534AB7' },
  comercio:  { norte: '#E07B4A', sul: '#F4B48C', leste: '#A8431B', oeste: '#F4B48C', 'centro-oeste': '#E07B4A', 'centro-sul': '#A8431B' },
  seguranca: { norte: '#9CC4DF', sul: '#5A93C2', leste: '#DCEBF4', oeste: '#5A93C2', 'centro-oeste': '#9CC4DF', 'centro-sul': '#0C447C' },
};

/** Rotulo/icone/cor de cada categoria de equipamento (assets/explore.js 70-76). */
export const CAT_META: Record<ExplorePoiCategory, { readonly label: string; readonly icon: string; readonly color: HexColor }> = {
  escola:    { label: 'Escolas',   icon: 'ti-school',         color: '#185FA5' },
  saude:     { label: 'Saúde',     icon: 'ti-heart-plus',     color: '#1D9E75' },
  seguranca: { label: 'Segurança', icon: 'ti-shield-half',    color: '#534AB7' },
  comercio:  { label: 'Comércio',  icon: 'ti-building-store', color: '#BA7517' },
  obra:      { label: 'Obras',     icon: 'ti-traffic-cone',   color: '#C2410C' },
};

/** Rotulo/icone de cada tipo de acao de campo (assets/explore.js 77-83). */
export const ACAO_META: Record<ExploreActionType, { readonly label: string; readonly icon: string }> = {
  comicio:   { label: 'Comício',   icon: 'ti-microphone-2' },
  caminhada: { label: 'Caminhada', icon: 'ti-walk' },
  reuniao:   { label: 'Reunião',   icon: 'ti-users' },
  visita:    { label: 'Visita',    icon: 'ti-door-enter' },
  carreata:  { label: 'Carreata',  icon: 'ti-car' },
};

/**
 * Ids das cidades-chave com rotulo permanente no mapa (assets/explore.js 85-86).
 * Limitar os labels permanentes evita poluicao visual em 62 municipios.
 */
export const MAJOR_LABEL_IDS: readonly MunicipalityId[] = [
  '1302603', '1303403', '1301902', '1304203', '1301209', '1304062',
  '1303809', '1302504', '1301704', '1302900', '1302405', '1302702', '1300409', '1301407',
];
